import { useCallback, useEffect, useState } from 'react';
import { commerceRequest } from '../lib/commerceApi';
import { isEmail, isIndianMobile } from '../lib/contact';
import { Quote, Payer } from '../types/commerce';

const QUOTE_DEBOUNCE_MS = 350;

export interface CheckoutQuoteOptions {
  readonly items: readonly { inventory_id: string; quantity: number }[];
  readonly addressId: string;
  readonly payer: Payer;
  readonly points: number;
  readonly enabled: boolean;
  /** Keep the accepted quote while a payment is in progress. */
  readonly frozen?: boolean;
  /** Changing this value requests a fresh quote (for example after a payment release). */
  readonly refreshKey?: number;
}

export default function useCheckoutQuote({
  items,
  addressId,
  payer,
  points,
  enabled,
  frozen = false,
  refreshKey = 0,
}: CheckoutQuoteOptions) {
  const [quotedBody, setQuotedBody] = useState('');
  const [quote, setQuote] = useState<Quote | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const body = JSON.stringify({
    items: items.map(({ inventory_id, quantity }) => ({
      inventory_id,
      quantity,
    })),
    delivery_address_id: addressId,
    payer,
    requested_points: points,
  });
  // The API rejects partial contact details with 422, so wait until they are complete.
  const ready =
    enabled &&
    !!addressId &&
    items.length > 0 &&
    !!payer.name &&
    isEmail(payer.email) &&
    isIndianMobile(payer.phone);
  useEffect(() => {
    if (frozen) return;
    setQuote(null);
    setError('');
    if (!ready) {
      setBusy(false);
      return;
    }
    const controller = new AbortController();
    setBusy(true);
    const timer = setTimeout(() => {
      commerceRequest<Quote>('/checkout/quote', {
        method: 'POST',
        body,
        signal: controller.signal,
      })
        .then((v) => {
          if (!controller.signal.aborted) {
            setQuotedBody(body);
            setQuote(v);
          }
        })
        .catch((e) => {
          if (!controller.signal.aborted)
            setError(e instanceof Error ? e.message : 'Could not calculate checkout');
        })
        .finally(() => {
          if (!controller.signal.aborted) setBusy(false);
        });
    }, QUOTE_DEBOUNCE_MS);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [frozen, body, ready, revision, refreshKey]);
  useEffect(() => {
    if (!quote || frozen) return;
    const timer = setTimeout(
      () => setRevision((v) => v + 1),
      Math.max(0, Date.parse(quote.expires_at) - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [quote, frozen]);
  const refresh = useCallback(() => setRevision((v) => v + 1), []);
  return {
    quote: quotedBody === body ? quote : null,
    error,
    busy,
    refresh,
  };
}
